type Resettable<T> = T & { reset: () => void };

/**
 * Ensures that the given async function only runs once at a time.
 * If called while promise from a previous call is pending, it will return the previous promise.
 *
 * @example
 * ```ts
 * const newFunc = dedupePromise(async (arg1, arg2) => {
 *   const result = await somethingAsync();
 *   return result;
 * });
 *
 * await newFunc(val1, val2);
 * ```
 */
export const dedupePromise = <Args extends unknown[], Ret>(
  factory: (...args: Args) => Promise<Ret>
): Resettable<(...args: Args) => Promise<Ret>> => {
  let activePromise: null | Promise<Ret> = null;

  const deduped = async (...args: Args) => {
    if (activePromise) return activePromise;

    activePromise = factory(...args);
    return activePromise.finally(() => {
      activePromise = null;
    });
  };

  deduped.reset = () => {
    activePromise = null;
  };

  return deduped;
};
